import React, { Fragment, ReactNode } from 'react';
import i18n from 'i18n';
import {
  MenuItem, Button, Label, HTMLSelect, Checkbox,
  Position, FormGroup, InputGroup, TextArea, RadioGroup, IconName,
} from '@blueprintjs/core';
import {
  Select, Classes, MultiSelect,
} from '@blueprintjs/select';
import { TimezonePicker, TimezoneDisplayFormat } from '@blueprintjs/timezone';
import { DateInput, TimePrecision, Classes as ClassesDateInput } from '@blueprintjs/datetime';
import MomentLocaleUtils from 'react-day-picker/moment';
import moment from 'moment';
import { FormTemplateField, FormTemplateFieldMap, FormTemplateOption } from 'data/template/template.types';
import { defaultTypeaheadPredicate, defaultTypeaheadRenderer, Typeahead } from './custom/typeahead'; // eslint-disable-line
import { defaultultiselectTypeaheadPredicate, defaultMultiselectTypeaheadRenderer, MultiselectTypeahead } from './custom/multiselectTypeahead';
import AutoGrowTextInput from '../textInput/AutoGrowTextInput';
import { getDateTimeFormatTemplate } from '../../utils';
import TextSuggest from './custom/TextSuggest';
import AdvancedSelect from './custom/AdvancedSelect';
import AdvancedMultiSelect from './custom/AdvancedMultiSelect';
import MultiSelectCustom from './custom/MultiSelectCustom';
import TagInputCustom from './custom/TagInputCustom';
import Form, { EMPTY_FIELD } from '.';
import {
  localizeGroup,
  localizeLabel,
  localizeOptions,
  localizePlaceholder,
} from './form.i18n';

export const createSelect = (arr: Array<any>, key: string, label: string) => {
  if (!arr.length) return [];
  return arr.map((entry: any) => ({ value: entry[key], label: entry[label] }));
};

export const defaultSelectOption = 'Select';

function wrapField(
  label: string | undefined,
  jsx: ReactNode,
  required: boolean,
  index: number,
  errorMessage: ReactNode,
  hintMessage: ReactNode,
  htmlFor?: string,
) {
  return (
    <div key={index}>
      <Label htmlFor={htmlFor}>
        {
          (label === undefined || label === '') ?
            required ? <span className="required-field">*</span> : <span>&nbsp;</span>
            :
            required ? <Fragment> {label}:<span className="required-field">*</span> </Fragment> : <span>{label}:</span>
        }
      </Label>
      {jsx}
      {hintMessage}
      {errorMessage}
    </div>
  );
}

function renderErrorMessage(ctx: Form, name: string) {
  return (
    <Fragment>
      {(ctx.state.fields[name].validations && ctx.state.fields[name].validations.errorMessage && ctx.state.fields[name].validations.errorMessage !== '') ?
        <Label className="field-error-message"> {ctx.state.fields[name].validations.errorMessage} </Label> : <Fragment />}
    </Fragment>
  );
}

function renderHintMessage(hintMessage?: string) {
  if (hintMessage == null) {
    return null;
  }

  return (
    <Fragment>
      <Label className="field-hint-message"> {hintMessage} </Label>
    </Fragment>
  );
}

function renderField(
  ctx: Form,
  field: FormTemplateField,
  name: string,
  value: any,
  index: number,
  isFormGroupField = false,
) {

  const {
    type,
    icon,
    tabIndex,
    filterable,
    disabled,
    readOnly,
    position,
    readOnlyIfOneOption,
    placeholder: defaultPlaceholder,
    inline,
    minimal,
    intent,
    customComponent,
    allowCreate,
    hintMessage,
    priority
  } = field;

  let fieldValue = value;
  const stateField = ctx.state.fields[name];
  let maxLength = 255;
  if ((type === 'text' || type === 'autoGrowText' || type === 'textarea') && stateField.validation && stateField.validation.validators) {
    const lengthValidator = (stateField.validation.validators as Array<any>).find((e: any) => e.type === 'length');
    if (lengthValidator) {
      maxLength = lengthValidator.length;
    }
  }
  const required = stateField.validation && stateField.validation.required;
  const className = 'input-maxwidth';
  const checkboxAdditionalStyle = isFormGroupField ? { margin: '0px' } : {};
  const errorMessageJSX = renderErrorMessage(ctx, name);
  const hintMessageJSX = renderHintMessage(hintMessage);
  const dataTestName = `input-${name}`;
  const { htmlId } = ctx.state.fields[name];
  const { label: defaultLabel } = ctx.state.name === 'filters' ? field : ctx.state.fields[name];
  const { i18nKeyPrefix, localizationDisabled } = ctx.props;

  const label = localizeLabel(name, defaultLabel, i18nKeyPrefix, localizationDisabled);
  const placeholder = localizePlaceholder(name, defaultPlaceholder, i18nKeyPrefix, localizationDisabled);
  const options = localizeOptions(name, field, i18nKeyPrefix, localizationDisabled);

  if (!type) return null;
  switch (type) {
    case 'text':
      return wrapField(label, <InputGroup
        data-test={dataTestName}
        autoComplete="off"
        autoCorrect="off"
        name={name}
        id={htmlId}
        tabIndex={tabIndex}
        disabled={disabled || readOnly}
        className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
        onChange={ctx.handleChange}
        onBlur={ctx.handleTouched}
        type={type}
        maxLength={maxLength}
        leftIcon={icon as IconName}
        value={fieldValue || ''}
        placeholder={placeholder}
      />, required, index, errorMessageJSX, hintMessageJSX, htmlId);

    case 'textSuggest':
      return wrapField(label, (
        <TextSuggest
          dataTestName={dataTestName}
          disabled={(disabled || readOnly) ?? false}
          className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
          items={options ?? []}
          fieldValue={fieldValue}
          name={name}
          placeholder={placeholder}
          onItemSelect={ctx.handleTextSuggestChange.bind(ctx, { name, value: fieldValue })}
          onQueryChange={ctx.handleTextSuggestQueryChange.bind(ctx, { name, value: fieldValue })}
          onBlur={ctx.handleTouched.bind(ctx, { currentTarget: { name, type, value: fieldValue } })}
          onFocus={ctx.handleTextSuggestFocus.bind(ctx, { currentTarget: { name, type, value: fieldValue } })}
        />
      ), required, index, errorMessageJSX, hintMessageJSX);

    case 'autoGrowText':
      return wrapField(label, <AutoGrowTextInput
        data-test={dataTestName}
        inputGroupProps={{
          autoComplete: 'off',
          autoCorrect: 'off',
          name,
          id: htmlId,
          tabIndex,
          className: ctx.getFieldClass(name, className, readOnly, value, disabled),
          onChange: ctx.handleChange,
          onBlur: ctx.handleTouched,
          type: 'text',
          maxLength,
          leftIcon: icon as IconName,
          disabled: (disabled || readOnly),
          value: (fieldValue || ''),
        }}
        maxWidth={ctx.props.parentSize && ctx.props.parentSize.width ?
          ctx.props.parentSize.width : null}
      />, required, index, errorMessageJSX, hintMessageJSX, htmlId);
    case 'autoGrowPassword':
      return wrapField(label, <AutoGrowTextInput
        data-test={dataTestName}
        inputGroupProps={{
          autoComplete: 'off',
          autoCorrect: 'off',
          name,
          id: htmlId,
          tabIndex,
          className: ctx.getFieldClass(name, className, readOnly, value, disabled),
          onChange: ctx.handleChange,
          type: 'password',
          maxLength,
          leftIcon: icon as IconName,
          disabled: (disabled || readOnly),
          value: (fieldValue || ''),
        }}
        maxWidth={ctx.props.parentSize && ctx.props.parentSize.width ?
          ctx.props.parentSize.width : null}
      />, required, index, errorMessageJSX, hintMessageJSX, htmlId);
    case 'password':
      return wrapField(label, <InputGroup
        data-test={dataTestName}
        autoComplete="off"
        autoCorrect="off"
        name={name}
        id={htmlId}
        tabIndex={tabIndex}
        className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
        onChange={ctx.handleChange}
        onBlur={ctx.handleTouched}
        onFocus={ctx.handleFocus}
        type={type}
        leftIcon={icon as IconName}
        disabled={disabled || readOnly}
        value={fieldValue || ''}
      />, required, index, errorMessageJSX, hintMessageJSX, htmlId);
    case 'textarea':
      return wrapField(label, <TextArea
        data-test={dataTestName}
        autoComplete="off"
        autoCorrect="off"
        name={name}
        id={htmlId}
        fill
        tabIndex={tabIndex}
        className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
        onChange={ctx.handleChange}
        maxLength={maxLength}
        onBlur={ctx.handleTouched}
        disabled={disabled || readOnly}
        value={fieldValue || ''}
      />, required, index, errorMessageJSX, hintMessageJSX, htmlId);
    case 'select':
      if (readOnlyIfOneOption && options?.length === 1 && options[0].value !== 'undefined' && options[0].value !== '') {
        return wrapField(label, <InputGroup
          data-test={dataTestName}
          autoComplete="off"
          autoCorrect="off"
          tabIndex={tabIndex}
          name={name}
          id={htmlId}
          className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
          value={options[0].label}
          readOnly
        />, required, index, errorMessageJSX, hintMessageJSX, htmlId);
      }
      return wrapField(label, <HTMLSelect
        data-test={dataTestName}
        autoComplete="off"
        autoCorrect="off"
        name={name}
        id={htmlId}
        tabIndex={tabIndex}
        className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
        options={options}
        onChange={ctx.handleChange}
        onBlur={ctx.handleTouched}
        disabled={disabled || readOnly}
        value={fieldValue || ''}
      />, required, index, errorMessageJSX, hintMessageJSX, htmlId);

    case 'advancedSelect':
      return wrapField(label, <AdvancedSelect
        data-testid={dataTestName}
        name={name}
        id={htmlId}
        tabIndex={tabIndex}
        // className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
        className={`${ctx.getFieldClass(name, className, readOnly, value, disabled)} advancedSelect`}
        options={options ?? []}
        onChange={ctx.handleAdvancedSelectChange}
        onBlur={ctx.handleAdvancedSelectBlur}
        onCreateNew={ctx.handleAdvancedSelectCreateNew}
        placeholder={placeholder ?? ''}
        disabled={disabled || readOnly}
        value={fieldValue ?? ''}
        allowCreate={allowCreate ?? false}
      />, required, index, errorMessageJSX, hintMessageJSX, htmlId);

    case 'advancedMultiSelect':
      let availableOptions: FormTemplateOption[] = Object.assign([], options?.sort((first: any, next: any) => {
        if (first.value < next.value) { return -1; }
        if (first.value > next.value) { return 1; }
        return 0;
      }));
      if (fieldValue) {
        const fieldValues = fieldValue.map((f: FormTemplateOption) => f.value);
        availableOptions = options?.filter((e: { label: any; value: any }) => !fieldValues.includes(e.value)) ?? [];
      }

      return wrapField(label, <AdvancedMultiSelect
        data-testid={dataTestName}
        name={name}
        id={htmlId}
        tabIndex={tabIndex}
        // className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
        className={`${ctx.getFieldClass(name, className, readOnly, value, disabled)} advancedSelect`}
        options={availableOptions ?? []}
        onChange={ctx.handleAdvancedMultiselectChange}
        onBlur={ctx.handleAdvancedMultiSelectBlur}
        onCreateNew={ctx.handleAdvancedSelectCreateNew}
        query={ctx.state.fields[name].query}
        placeholder={placeholder ?? ''}
        disabled={disabled || readOnly}
        fieldValue={fieldValue ?? []}
        onRemoveTag={ctx.handleAdvancedMultiselectItemRemove}
        allowCreate={allowCreate ?? false}
      />, required, index, errorMessageJSX, hintMessageJSX, htmlId);
    case 'multiSelectCustom':
      const multiSelectOptions = (options || []).filter((x) => !!x);
      return wrapField(label, (
        <MultiSelectCustom
          key={name}
          dataTestName={dataTestName}
          disabled={(disabled || readOnly) ?? false}
          className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
          items={multiSelectOptions}
          fieldValue={fieldValue as MultiselectTypeahead[] ?? [] as MultiselectTypeahead[]}
          name={name}
          priority={priority}
          placeholder={placeholder}
          onItemSelect={ctx.handleCustomMultiSelectSelectChange}
        />
      ), required, index, errorMessageJSX, hintMessageJSX);

    case 'tagInputCustom':
      return wrapField(label, (
        <TagInputCustom
          placeholder={placeholder}
          disabled={disabled || readOnly}
          value={value}
          name={name}
          onChange={ctx.handleTagInputCustomChange}
        />
      ), required, index, errorMessageJSX, hintMessageJSX);

    case 'radio':
      const radioComponent = (
        <div>
          <RadioGroup
            inline={inline}
            data-test={dataTestName}
            name={name}
            className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
            options={options}
            onChange={ctx.handleChange}
            disabled={disabled || readOnly}
            selectedValue={fieldValue || ''}
          />
          {errorMessageJSX}
        </div>
      );

      if (!label) {
        return radioComponent;
      }

      return wrapField(label, radioComponent, required, index, errorMessageJSX, hintMessageJSX);

    case 'typeahead':
      const SelectTypeahead = Select.ofType<Typeahead>();
      const selectedText =
        options?.filter((item: any) => item.value === ctx.state.fields[name].value)[0].label;
      return wrapField(label, (
        <SelectTypeahead
          data-test={dataTestName}
          disabled={disabled || readOnly}
          itemRenderer={defaultTypeaheadRenderer}
          itemPredicate={defaultTypeaheadPredicate}
          className={`${Classes.SELECT} ${className}`}
          filterable={filterable}
          items={options ?? []}
          query={ctx.state.fields[name].query}
          noResults={<MenuItem disabled text={i18n.t('common.form.noResults')} />}
          onItemSelect={ctx.handleTypeaheadChange.bind(ctx, { name, value: fieldValue })}
          activeItem={fieldValue}
        >
          <Button
            fill
            text={selectedText}
          />
        </SelectTypeahead>
      ), required, index, errorMessageJSX, hintMessageJSX);
    case 'number':
      return wrapField(label, <InputGroup
        data-test={dataTestName}
        autoComplete="off"
        autoCorrect="off"
        tabIndex={tabIndex}
        name={name}
        id={htmlId}
        className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
        onChange={ctx.handleChange}
        onBlur={ctx.handleTouched}
        type={type}
        leftIcon={icon as IconName}
        disabled={disabled}
        value={fieldValue || 0}
        min={0}
        readOnly={readOnly}
      />, required, index, errorMessageJSX, hintMessageJSX, htmlId);
    case 'datetime':
      const timePrecision = field.timePrecision ? field.timePrecision : TimePrecision.MINUTE;

      const component = (
        <DateInput
          data-test={dataTestName}
          dayPickerProps={{
            className: name,
            locale: i18n.language,
            localeUtils: MomentLocaleUtils,
          }}
          value={fieldValue ? new Date(fieldValue) : null}
          inputProps={{
            tabIndex,
            name,
            autoCorrect: 'off',
            autoComplete: 'off',
            id: htmlId,
            leftIcon: icon as IconName,
          }}
          className={`${ClassesDateInput.DATEINPUT} ${className}`}
          popoverProps={{ position: Position.BOTTOM }}
          formatDate={(date) => moment(date).format(getDateTimeFormatTemplate())}
          parseDate={(str) => moment(str, getDateTimeFormatTemplate()).toDate()}
          onChange={ctx.handleDateTimeChange.bind(ctx, { name, value: fieldValue })}
          disabled={disabled || readOnly}
          timePrecision={timePrecision}
          maxDate={moment(new Date()).add(20, 'year').toDate()}
        />
      );

      return wrapField(label, component, required, index, errorMessageJSX, hintMessageJSX, htmlId);
    case 'checkbox':
      return (
        <Fragment><Checkbox
          data-test={dataTestName}
          tabIndex={tabIndex}
          key={index}
          name={name}
          className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
          label={label}
          onBlur={ctx.handleTouched}
          onChange={ctx.handleChange}
          checked={!!fieldValue}
          disabled={disabled || readOnly}
          style={required
            ? { display: 'inline-block', width: 'auto', ...checkboxAdditionalStyle }
            : checkboxAdditionalStyle}
        />
          {required && <span className="required-field">*</span>}
        </Fragment>
      );
    case 'timezone':
      return wrapField(label, <TimezonePicker
        data-test={dataTestName}
        className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
        onChange={ctx.handleTimeZoneChange.bind(ctx, { name, value: fieldValue })}
        popoverProps={{
          position: Position.BOTTOM,
          popoverClassName: 'popover-scroll',
        }}
        valueDisplayFormat={TimezoneDisplayFormat.NAME}
        buttonProps={{ fill: true }}
        disabled={disabled || readOnly}
        value={fieldValue || ''}
      />, required, index, errorMessageJSX, hintMessageJSX);
    case 'multiselectTypeahead':
      let optionsAvailable: FormTemplateOption[] = Object.assign([], options?.sort((first: any, next: any) => {
        if (first.value < next.value) { return -1; }
        if (first.value > next.value) { return 1; }
        return 0;
      }));
      if (fieldValue === undefined) {
        fieldValue = [];
      } else {
        optionsAvailable = options?.filter((e: { label: any; value: any }) =>
          !fieldValue.includes(e.value)) ?? [];
        fieldValue = options?.filter((e: { label: any; value: any }) =>
          fieldValue.includes(e.value));
      }
      const MultiselectTypeaheadComponent = MultiSelect.ofType<MultiselectTypeahead>();
      return wrapField(label,
        <div tabIndex={tabIndex}>
          <MultiselectTypeaheadComponent
            data-test={dataTestName}
            itemPredicate={defaultultiselectTypeaheadPredicate}
            itemRenderer={defaultMultiselectTypeaheadRenderer}
            className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
            tagRenderer={(item: any) => item.label}
            items={optionsAvailable}
            query={ctx.state.fields[name].query}
            noResults={<MenuItem disabled text={i18n.t('common.form.noResults')} />}
            placeholder={i18n.t('common.form.search')}
            tagInputProps={{
              disabled: disabled || readOnly,
              onRemove: ctx.handleMultiselectTypeaheadItemRemove
                .bind(ctx, { name, value: fieldValue }),
            }}
            popoverProps={{
              minimal: true,
              popoverClassName: 'popover-scroll',
              position: position as Position || Position.BOTTOM,
            }}
            resetOnSelect
            onItemSelect={ctx.handleMultiselectTypeaheadChange.bind(ctx, { name, value: fieldValue })}
            selectedItems={fieldValue}
          />
        </div>, required, index, errorMessageJSX, hintMessageJSX);
    case 'timeInterval':
      return wrapField(label, (
        <div>
          <HTMLSelect
            data-test={dataTestName}
            autoComplete="off"
            autoCorrect="off"
            defaultValue="0"
            name={`${name}-Hours`}
            tabIndex={tabIndex}
            disabled={disabled || readOnly}
            className={ctx.getFieldClass(name, '', readOnly, value, disabled)}
            options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
              16, 17, 18, 19, 20, 21, 22, 23, 24]}
            onChange={ctx.handletimeIntervalChange}
            value={ctx.getTimeIntervalValue(fieldValue, 'Hours')}
          />
          <span style={{ paddingRight: '10px' }}> {i18n.t('common.form.hours')}</span>
          <HTMLSelect
            data-test={dataTestName}
            autoComplete="off"
            autoCorrect="off"
            defaultValue="0"
            name={`${name}-Mins`}
            tabIndex={tabIndex}
            disabled={disabled || readOnly}
            className={ctx.getFieldClass(name, '', readOnly, value, disabled)}
            options={[0, 15, 30, 45]}
            onChange={ctx.handletimeIntervalChange}
            value={ctx.getTimeIntervalValue(fieldValue, 'Mins')}
          />
          <span> {i18n.t('common.form.minutes')}</span>
        </div>
      ), required, index, errorMessageJSX, hintMessageJSX);
    case 'label':
      return wrapField(label,
        <Label
          data-test={dataTestName}
          className={`label-field label-field-${name}`}
        >
          {fieldValue}
        </Label>, required, index, errorMessageJSX, hintMessageJSX);
    case 'detailsButton':
      const defaultDetailsButtonLabel = (field.initial ?? '').toString();
      const detailsButtonLabel = localizeLabel(name, defaultDetailsButtonLabel, i18nKeyPrefix, localizationDisabled);

      return wrapField(label,
        <Button
          intent={intent}
          minimal={minimal}
          icon={icon as IconName}
          data-test={dataTestName}
          text={detailsButtonLabel}
          onClick={field.handleClick}
        />, required, index, errorMessageJSX, hintMessageJSX);

    // Created field type 'button' to get the flexibility to disable since details button
    // shouldn't be disabled even in view scenarios to render popups
    case 'button':
      const defaultButtonLabel = (field.initial ?? '').toString();
      const buttonLabel = localizeLabel(name, defaultButtonLabel, i18nKeyPrefix, localizationDisabled);

      return wrapField(label,
        <Button
          intent={intent}
          minimal={minimal}
          icon={icon as IconName}
          data-test={dataTestName}
          text={buttonLabel}
          onClick={field.handleClick}
          className={ctx.getFieldClass(name, className, readOnly, value, disabled)}
          disabled={disabled || readOnly}
        />, required, index, errorMessageJSX, hintMessageJSX);

    case 'buttonWithMessage':
      const defaultButtonWithMessageLabel = (field.initial ?? '').toString();
      const buttonWithMessageLabel = localizeLabel(
        name,
        defaultButtonWithMessageLabel,
        i18nKeyPrefix,
        localizationDisabled,
      );

      return (
        <div className="form-flex-row-css">
          <Button
            data-test={dataTestName}
            text={buttonWithMessageLabel}
            onClick={field.handleClick}
          />
          <Label>
            {field.message ?? ''}
          </Label>
        </div>
      );

    case 'custom':

      const CustomComponent = ctx.props.customFieldComponentMap?.[customComponent ?? ''];

      if (CustomComponent == null) {
        return null;
      }

      const customComponentElement = (
        <CustomComponent
          value={fieldValue || ''}
          label={label}
          placeholder={placeholder}
        />
      );

      if (!label) {
        return (
          customComponentElement
        );
      }

      return wrapField(label, customComponentElement, required, index, errorMessageJSX, hintMessageJSX, htmlId);

    default:
      break;
  }
  return null;
}

export function renderFields(ctx: Form, rowLayout: any, isFormGroupFields = false, formGroupFlexFlow = 'row'): ReactNode {
  const {
    fields,
    flexFlow = 'row',
    cssClassName = '',
  } = ctx.props;

  if (isFormGroupFields) {
    const formGroupClassName = formGroupFlexFlow === 'row' ? 'form-flex-row-css' : 'form-flex-col-css';
    return (
      <div className={formGroupClassName}>
        {renderDivElements(rowLayout, ctx, fields, isFormGroupFields)}
      </div>
    );
  }

  const className = `${flexFlow === 'row' ? 'form-flex-row-css' : 'form-flex-col-css'} ${cssClassName}`;
  return (
    <form autoComplete="off" autoCorrect="off">
      <div className={className}>
        {renderDivElements(rowLayout, ctx, fields, isFormGroupFields)}
      </div>
    </form>
  );
}

function renderDivElements(rowLayout: string[], ctx: Form, fields: FormTemplateFieldMap, isFormGroupFields: boolean) {
  return rowLayout.map((rowLayoutEntry: any, j: number) => {
    if (typeof (rowLayoutEntry) === 'object' && ('group' in rowLayoutEntry)) {
      if (rowLayoutEntry.hide === 'true') {
        return (
          <Fragment key={j} />
        );
      }

      return (
        <div className="field-group" key={j}>
          {renderGroup(ctx, rowLayoutEntry, j)}
        </div>
      );
    }

    if (rowLayoutEntry === EMPTY_FIELD) {
      // Indicates that the field should be rendered as a empty field
      return (
        <div key={j} className="field field-empty" />
      );
    }

    const {
      value,
      hide,
      className = '',
    } = ctx.state.fields[rowLayoutEntry];

    const {
      renderAsEmptyIfHidden,
      className: fieldClassName = '',
    } = fields[rowLayoutEntry];

    try {
      if (hide === 'true') {
        if (renderAsEmptyIfHidden) {
          return (
            <div key={j} className="field" />
          );
        }

        return (
          <Fragment key={j} />
        );
      }

      return (
        <div key={j} className={`field ${className} ${fieldClassName}`} onKeyPress={ctx.handleKeyPress.bind(ctx)}>
          {renderField(ctx, fields[rowLayoutEntry], rowLayoutEntry, value, j, isFormGroupFields)}
        </div>
      );
    } catch (err) {
      throw Error(`Problem rendering form fields. Check JSON configuration: ${rowLayoutEntry}`);
    }
  });
}

function renderGroup(ctx: Form, groupObj: any, index: number) {
  const { i18nKeyPrefix, localizationDisabled } = ctx.props;
  const defaultLabel = groupObj.group;

  const label = localizeGroup(
    defaultLabel,
    i18nKeyPrefix,
    localizationDisabled,
  );

  return (
    <FormGroup
      key={index}
      label={label}
      style={(groupObj.group === '') ? { border: 'none' } : {}}
    >
      {renderFields(ctx, groupObj.fields, true, groupObj.flexDirection !== undefined ? groupObj.flexDirection : 'column')}
    </FormGroup>
  );
}
